/**
 * This utility is modeled after the well-established PHP utility of the same
 * name, Url::isVimeoRedirectableUrl.
 *
 * It validates redirect URLs by blocking dangerous URI schemes (javascript:,
 * data:, etc.), allowing relative URLs, and allowlisting absolute URLs whose
 * host matches known Vimeo domains.
 */

/**
 * Returns whether a url string is absolute.
 */
function isUrlAbsolute(url: string): boolean {
  const regex = /^https?:\/\/|^\/\//i;
  return regex.test(url);
}

/**
 * Returns whether a url string is relative.
 */
function isUrlRelative(url: string): boolean {
  // Reject anything with a scheme (e.g. javascript:, data:)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/m.test(url.trim())) {
    return false;
  }
  return !isUrlAbsolute(url) && url[0] === '/';
}

/**
 * Check if the url's host is part of the global allow list.
 */
function isUrlHostInAllowList(url: string): boolean {
  const allowListHosts = [
    'ci.vimeows.com',
    'help.vimeo.com',
    'vimeo.com',
    'vimeo.dev',
    'vimeo.zendesk.com',
    'vimeopro.com',
    'vimeotest.dev',
    // Venues domains
    'feature-slvhyk3sjq-ue.a.run.app',
    'spa-v2-sim-2-slvhyk3sjq-ue.a.run.app',
    'dev.venues.vimeows.com',
    'venues.vimeo.com',
    'localhost',
    // Player domains
    'player2.vimeo.dev', // dev
    'master.playerci.vimeows.com', // ci
    'playerci.vimeows.com', // ci (test branch)
    'player.vimeo.com', // prod
  ];

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch (e) {
    // Invalid url.
    return false;
  }

  return (
    allowListHosts.includes(parsedUrl.host) ||
    allowListHosts.includes(parsedUrl.hostname) ||
    allowListHosts.some(
      (host) => parsedUrl.host.endsWith(`.${host}`) || parsedUrl.hostname.endsWith(`.${host}`),
    )
  );
}

/**
 * Fully decode a URL to prevent multi-encoding bypass of scheme checks.
 */
function fullyDecode(url: string): string {
  let decoded = url;
  for (let i = 0; i < 10; i++) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
}

/**
 * Returns true if the URL uses a dangerous scheme (javascript:, data:, etc.)
 * after full decoding and control character stripping.
 */
function hasDangerousScheme(url: string): boolean {
  const normalized = fullyDecode(url)
    .replace(/[\x00-\x1f\x7f]/g, '')
    .trim();
  return /^(javascript|data|vbscript|blob|about)\s*:/i.test(normalized);
}

/**
 * Returns whether a url string is a valid Vimeo redirect url. Accepts relative
 * urls and absolute urls whose host is in the Vimeo domain allowlist.
 */
export function isVimeoRedirectableUrl(url: string, customAllowList: string[] = []): boolean {
  // Block dangerous URI schemes (e.g. javascript:) regardless of encoding depth.
  if (hasDangerousScheme(url)) {
    return false;
  }

  // If the url is found is the passed customAllowList, return true.
  if (customAllowList.length && customAllowList.includes(url)) {
    return true;
  }

  // If the url is relative, return true.
  if (url && isUrlRelative(url)) {
    return true;
  }

  // If the url's host is in our allow list, return true.
  return isUrlHostInAllowList(url);
}
